import { QuestionType } from '@webapp/common/resources/survey';

import type { SurveyQuestion } from '../lib';
import { ABTesting } from '../types/ab-testing';
import { Agreement } from '../types/agreement';
import { CardSorting } from '../types/card-sorting';
import { Conjoint } from '../types/conjoint';
import { Csi } from '../types/csi';
import { Datetime } from '../types/datetime';
import { Dropdown } from '../types/dropdown';
import { Email } from '../types/email';
import { FigmaTest } from '../types/figma-test';
import { FileUpload } from '../types/file-upload';
import { FirstClick } from '../types/first-click';
import { FiveSecTest } from '../types/five-sec-test';
import { GroupFreeAnswers } from '../types/group-free-answers';
import { Kano } from '../types/kano';
import { ListSelect } from '../types/list-select';
import { LongText } from '../types/long-text';
import { MatrixDropdown } from '../types/matrix-dropdown';
import { MatrixRating } from '../types/matrix-rating';
import { MatrixSelect } from '../types/matrix-select';
import { MatrixText } from '../types/matrix-text';
import { MaxDiff } from '../types/max-diff/max-diff';
import { Name } from '../types/name';
import { NestedList } from '../types/nested-list';
import { Nps } from '../types/nps';
import { Number } from '../types/number';
import { PairComparison } from '../types/pair-comparison';
import { Phone } from '../types/phone';
import { Rank } from '../types/rank';
import { Residence } from '../types/residence';
import { Scale } from '../types/scale';
import { ScaleDistribute } from '../types/scale-distribute';
import { SelectImage } from '../types/select-image';
import { SemDiff } from '../types/sem-diff';
import { ShortText } from '../types/short-text';
import { Smile } from '../types/smile';
import { Star } from '../types/star';
import { TestText } from '../types/test-text';
import { TextBlock } from '../types/text-block';
import { TextPerception } from '../types/text-perception';
import { VideoTest } from '../types/video-test';

export const TypeView: VFC<SurveyQuestion> = ({
    twisterReadOnly,
    actor,
    question,
    questionBlockPosition,
    readOnly,
}) => {
    const commonProps = { actor, question, questionBlockPosition };
    switch (question.type) {
        case QuestionType.ONE_OF_LIST:
        case QuestionType.FEW_OF_LIST:
        case QuestionType.TEST_ONE_OF_LIST:
        case QuestionType.TEST_FEW_OF_LIST:
            return <ListSelect {...commonProps} readOnly={readOnly} />;
        case QuestionType.TEST_TEXT:
            return <TestText actor={actor} question={question} readOnly={readOnly} />;
        case QuestionType.SELECT_ONE_IMAGE:
        case QuestionType.SELECT_FEW_IMAGE:
            return <SelectImage actor={actor} question={question} />;
        case QuestionType.DROPDOWN_LIST:
            return <Dropdown actor={actor} question={question} />;
        case QuestionType.STAR:
            return <Star {...commonProps} />;
        case QuestionType.NUMBER:
            return <Number actor={actor} question={question} />;
        case QuestionType.SHORT_TEXT:
            return <ShortText actor={actor} question={question} />;
        case QuestionType.LONG_TEXT:
            return <LongText actor={actor} question={question} />;
        case QuestionType.TEXT_BLOCK:
            return <TextBlock {...commonProps} />;
        case QuestionType.GROUP_FREE_ANSWERS:
            return <GroupFreeAnswers {...commonProps} />;
        case QuestionType.EMAIL:
            return <Email {...commonProps} />;
        case QuestionType.AGREEMENT:
            return <Agreement actor={actor} question={question} />;
        case QuestionType.NPS:
            return <Nps actor={actor} question={question} />;
        case QuestionType.RANK:
        case QuestionType.TEST_RANK:
            return <Rank twisterReadOnly={twisterReadOnly} actor={actor} question={question} readOnly={readOnly} />;
        case QuestionType.SCALE:
            return <Scale actor={actor} question={question} />;
        case QuestionType.SEM_DIFF:
            return <SemDiff actor={actor} question={question} />;
        case QuestionType.DISTRIBUTE_SCALE:
            return <ScaleDistribute {...commonProps} />;
        case QuestionType.DATETIME:
            return <Datetime {...commonProps} />;
        case QuestionType.PHONE:
            return <Phone {...commonProps} />;
        case QuestionType.SMILE:
            return <Smile actor={actor} question={question} />;
        case QuestionType.RESIDENCE:
            return <Residence actor={actor} question={question} />;
        case QuestionType.NESTED_LIST:
            return <NestedList actor={actor} question={question} />;
        case QuestionType.FILE_UPLOAD:
            return <FileUpload actor={actor} question={question} />;
        case QuestionType.NAME:
            return <Name {...commonProps} />;
        case QuestionType.MATRIX_FEW_ANSWERS:
        case QuestionType.MATRIX_SINGLE_ANSWER:
            return <MatrixSelect actor={actor} question={question} />;
        case QuestionType.MATRIX_TEXT_ANSWER:
            return <MatrixText actor={actor} question={question} />;
        case QuestionType.MATRIX_RATING:
            return <MatrixRating {...commonProps} />;
        case QuestionType.CSI:
            return <Csi actor={actor} question={question} />;
        case QuestionType.MATRIX_DROPDOWN_LIST:
            return <MatrixDropdown actor={actor} question={question} />;
        case QuestionType.AB_TESTING:
            return <ABTesting {...commonProps} />;
        case QuestionType.TEXT_PERCEPTION:
            return <TextPerception {...commonProps} />;
        case QuestionType.PAIR_COMPARISON:
            return <PairComparison {...commonProps} />;
        case QuestionType.FIRST_CLICK:
            return <FirstClick {...commonProps} />;
        case QuestionType.CARD_SORTING:
            return <CardSorting {...commonProps} />;
        case QuestionType.MAX_DIFF:
            return <MaxDiff {...commonProps} />;
        case QuestionType.VIDEO_TEST:
            return <VideoTest {...commonProps} />;
        case QuestionType.FIVE_SEC_TEST:
            return <FiveSecTest {...commonProps} />;
        case QuestionType.FIGMA_TEST:
            return <FigmaTest {...commonProps} />;
        case QuestionType.KANO:
            return <Kano {...commonProps} />;
        case QuestionType.CONJOINT:
            return <Conjoint {...commonProps} />;
        default:
            return null;
    }
};
